import React, { useState } from 'react';

// Flags a run of 7+ digits (allowing spaces, dashes, dots, brackets)
// so ordinary text like "size 42" or "2 for 5,000" isn't flagged.
const looksLikePhoneNumber = (text) => /\+?\d[\d\s().-]{6,}\d/.test(text);

const MAX_NAME_LENGTH = 100;

const EditProductModal = ({ product, onSave, onClose }) => {
  const [name, setName] = useState(product.name || '');
  const [price, setPrice] = useState(String(product.price ?? ''));
  const [details, setDetails] = useState(product.details || '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    const trimmedName = name.trim();
    const numericPrice = Number(price);

    if (!trimmedName) e.name = 'Product name is required';
    else if (trimmedName.length > MAX_NAME_LENGTH) e.name = `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
    else if (looksLikePhoneNumber(trimmedName)) e.name = 'Phone numbers are not allowed in the product name';

    if (price === '' || Number.isNaN(numericPrice) || numericPrice <= 0) {
      e.price = 'Enter a valid price greater than 0';
    }

    if (!details.trim()) e.details = 'Details are required';
    else if (looksLikePhoneNumber(details)) e.details = 'Phone numbers are not allowed in product details';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (saving || !validate()) return;

    setSaving(true);
    try {
      await onSave(product._id, {
        name: name.trim(),
        price: Number(price),
        details: details.trim(),
      });
      onClose();
    } catch {
      // The parent already shows an error toast; keep the modal open
      // so the user doesn't lose what they typed.
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: '13px', fontWeight: 600, display: 'block', marginTop: '14px' };
  const fieldStyle = { width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', margin: '6px 0 0' };
  const errorStyle = { color: '#dc2626', fontSize: '12px', margin: '4px 0 0' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '16px' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: '100%', maxWidth: '420px', maxHeight: '85vh', overflowY: 'auto' }}
      >
        <h3 style={{ margin: '0 0 4px' }}>Edit product</h3>
        <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#888' }}>
          Currency ({product.currency || 'TZS'}) and category can't be changed.
        </p>

        <label style={{ ...labelStyle, marginTop: '10px' }}>Name</label>
        <input
          type="text"
          value={name}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setName(e.target.value)}
          style={fieldStyle}
        />
        {errors.name && <p style={errorStyle}>{errors.name}</p>}

        <label style={labelStyle}>Price ({product.currency || 'TZS'})</label>
        <input
          type="number"
          min="0"
          step="any"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          style={fieldStyle}
        />
        {errors.price && <p style={errorStyle}>{errors.price}</p>}

        <label style={labelStyle}>Details</label>
        <textarea
          rows={5}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          style={{ ...fieldStyle, resize: 'vertical' }}
        />
        {errors.details && <p style={errorStyle}>{errors.details}</p>}

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ flex: 1, padding: '10px', border: '1px solid #ccc', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{ flex: 1, padding: '10px', border: 'none', background: '#2563eb', color: '#fff', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProductModal;